/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable no-console */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import { API_URL } from '@/configs/api.config';

export interface IUseSocketOptions {
  token?: string | null;
  enabled?: boolean;
  autoReconnect?: boolean;
  isDebug?: boolean;
}

export interface IUseSocketReturn<
  ServerToClient extends Record<string, (...args: any[]) => void> = {},
  ClientToServer extends Record<string, (...args: any[]) => void> = {},
> {
  socket: Socket<ServerToClient, ClientToServer> | null;
  isConnected: boolean;
  error: Error | null;
  on: <K extends keyof ServerToClient & string>(
    event: K,
    callback: (...args: Parameters<ServerToClient[K]>) => void
  ) => void;
  off: <K extends keyof ServerToClient & string>(
    event: K,
    callback: (...args: Parameters<ServerToClient[K]>) => void
  ) => void;
  emit: <K extends keyof ClientToServer & string>(event: K, ...args: Parameters<ClientToServer[K]>) => boolean;
  connect: () => void;
  disconnect: () => void;
}

const SOCKET_CONFIG = {
  AUTO_CONNECT: false, // we handle retry manually
  RECONNECTION: false,
  TIMEOUT: 20000,
  FORCE_NEW: true,
  TRANSPORTS: ['websocket', 'polling'] as string[],
  UPGRADE: true,
  REMEMBER_UPGRADE: true,
} as const;

export function useSocket<
  ServerToClient extends Record<string, (...args: any[]) => void> = {},
  ClientToServer extends Record<string, (...args: any[]) => void> = {},
>({ token, enabled = true, autoReconnect = true, isDebug = false }: IUseSocketOptions = {}): IUseSocketReturn<
  ServerToClient,
  ClientToServer
> {
  // HOOKS
  const socketRef = useRef<Socket<ServerToClient, ClientToServer> | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Retry state
  const reconnectAttemptsRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Flag for intentional disconnects
  const intentionalDisconnectRef = useRef(false);

  // HELPERS
  const clearRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!autoReconnect || !token) return;

    const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 30000); // exponential backoff
    reconnectAttemptsRef.current += 1;

    console.log(`⏳ Retry in ${delay / 1000}s (attempt ${reconnectAttemptsRef.current})`);

    retryTimeoutRef.current = setTimeout(() => {
      if (!socketRef.current?.connected) {
        console.log('🔄 Reconnecting socket...');
        socketRef.current = createSocket(token);
        socketRef.current.connect();
      }
    }, delay);
  }, [autoReconnect, token]);

  const resetRetry = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    clearRetry();
  }, [clearRetry]);

  const createSocket = useCallback(
    (authToken: string) => {
      const socket = io(API_URL, {
        auth: { token: authToken },
        autoConnect: SOCKET_CONFIG.AUTO_CONNECT,
        reconnection: SOCKET_CONFIG.RECONNECTION,
        timeout: SOCKET_CONFIG.TIMEOUT,
        transports: SOCKET_CONFIG.TRANSPORTS,
        forceNew: SOCKET_CONFIG.FORCE_NEW,
        upgrade: SOCKET_CONFIG.UPGRADE,
        rememberUpgrade: SOCKET_CONFIG.REMEMBER_UPGRADE,
      }) as Socket<ServerToClient, ClientToServer>;

      socket.on('connect', () => {
        if (isDebug) {
          console.log('✅ Socket connected:', socket.id);
        }
        setIsConnected(true);
        setError(null);
        resetRetry();
      });

      socket.on('disconnect', (_reason) => {
        setIsConnected(false);
        if (intentionalDisconnectRef.current) {
          if (isDebug) {
            console.log('🔄 Socket intentionally disconnected by user');
          }
          intentionalDisconnectRef.current = false;
          return;
        }
        if (isDebug) {
          console.warn('⚠️ Socket disconnected:', _reason);
        }
        scheduleReconnect();
      });

      socket.on('connect_error', (err) => {
        if (isDebug) {
          console.error('❌ Socket connection error:', err.message);
        }
        setError(err);
        setIsConnected(false);
        scheduleReconnect();
      });

      return socket;
    },
    [resetRetry, scheduleReconnect]
  );

  const on = useCallback(
    <K extends keyof ServerToClient & string>(event: K, callback: (...args: Parameters<ServerToClient[K]>) => void) => {
      socketRef.current?.on(event, callback as any);
    },
    []
  );

  const off = useCallback(
    <K extends keyof ServerToClient & string>(event: K, callback: (...args: Parameters<ServerToClient[K]>) => void) => {
      socketRef.current?.off(event, callback as any);
    },
    []
  );

  const emit = useCallback(
    <K extends keyof ClientToServer & string>(event: K, ...args: Parameters<ClientToServer[K]>) => {
      if (!socketRef.current?.connected) return false;
      socketRef.current.emit(event, ...args);
      return true;
    },
    []
  );

  const connect = useCallback(() => {
    if (socketRef.current && !socketRef.current.connected) {
      socketRef.current.connect();
    }
  }, []);

  const disconnect = useCallback(() => {
    intentionalDisconnectRef.current = true;
    resetRetry();
    socketRef.current?.disconnect();
  }, [resetRetry]);

  // EFFECTS
  useEffect(() => {
    if (!enabled || !token) return;

    socketRef.current = createSocket(token);
    socketRef.current.connect();

    return () => {
      clearRetry();
      intentionalDisconnectRef.current = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [enabled, token, createSocket, clearRetry]);

  // DATA
  return useMemo(
    () => ({
      socket: socketRef.current,
      isConnected,
      error,
      on,
      off,
      emit,
      connect,
      disconnect,
    }),
    [isConnected, error, on, off, emit, connect, disconnect]
  );
}
